
import type { PropType } from 'vue'
import { computed, defineComponent, onMounted, reactive } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// new-services
import timeSlotsAPI from '@/new-services/time-slots.service'

// realisations
import { SLA } from '@/core/realisations/sla.realisation'

// types
import type { ReceiverT } from '@/core/types/receivers.types'
import type { TimeSlotForCreateOrder } from '@/new-services/types/time-slots'
import type { CompletedCargoT } from '@/core/types/create_order.types'

export default defineComponent({
    components: {
        TheButton,
        TheSelect,
        TheInput
    },
    props: {
        companyId: {
            type: Number as PropType<number | null>,
            default: null
        },
        senderCityId: {
            type: [Number, null] as PropType<number | null>,
            default: null
        },
        senderDataTime: {
            type: [String, null] as PropType<string | null>,
            default: null
        },
        cargo: {
            type: [Object, null] as PropType<CompletedCargoT | null>,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit'],
    setup(props, { emit }) {
        const date_state = reactive({
            is_loading: false,
            is_failed: false,
            minimumDeliveryDay: null as string | null,
            maximumDeliveryDay: null as string | null,
            date: null as string | null,
        })

        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TimeSlotForCreateOrder[],
            timeSlot: null as TimeSlotForCreateOrder | null
        })

        const formatFromTo = (from: string, to: string): { from: string; to: string } => {
            const formattedFrom = from.slice(0, 5)
            const [h, m, s] = to.split(':').map(Number)

            let endHour = h
            let endMinute = m

            if (m !== 0 || s !== 0) {
                endHour = (h + 1) % 24
                endMinute = 0
            }

            const formattedTo = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`

            return {
                from: formattedFrom,
                to: formattedTo,
            }
        }

        const onTimeSlotChange = (timeSlotId: number) => {
            if (props.disabled) return

            const timeSlot = state.list.find((t) => t.id === timeSlotId)
            state.timeSlot = timeSlot || null
        }

        const onSubmit = () => {
            if (props.disabled || !state.timeSlot) return

            emit('submit', {
                templateId: state.timeSlot.id,
                timeFromTo: `${state.timeSlot.timeFrom} - ${state.timeSlot.timeTo}`,
                deliveryDate: date_state.date,
            })
        }

        const onDateChange = async () => {
            if (!props.cargo) {
                return
            }

            const receiver = props.cargo.receiver as ReceiverT & { dispatcher_sector_id: number; }

            try {
                state.is_loading = true

                const response = await timeSlotsAPI.getTimeSlotsForCreateOrder({
                    companyId: props.companyId,
                    dispatcherSectorId: receiver.dispatcher_sector_id,
                    latitude: receiver.latitude,
                    longitude: receiver.longitude,
                    date: date_state.date,
                    typeId: 2,
                })

                state.list = response.data.data.map((ts) => {
                    const { from, to } = formatFromTo(ts.timeFrom, ts.timeTo)

                    return {
                        id: ts.id,
                        typeId: ts.typeId,
                        status: ts.status,
                        timeFrom: from,
                        timeTo: to
                    }
                })

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const loadMinimumDeliveryDay = async () => {
            if (!props.senderCityId || !props.companyId || !props.cargo || !props.senderDataTime) return

            try {
                date_state.is_loading = true

                const response = await SLA.calculateMinDeliveryDate({
                    fromCityId: props.senderCityId as number,
                    toCityId: props.cargo.receiver.city_id,
                    senderDataTime: props.senderDataTime,
                    shipmentTypeId: props.cargo.logistics_info.shipment_type,
                    companyId: props.companyId
                })

                const minDateStr = response.data.minimumDeliveryDay.slice(0, 10)
                const minDate = new Date(minDateStr)

                minDate.setDate(minDate.getDate() + 2)

                const maxDateStr = minDate.toISOString().slice(0, 10)

                date_state.minimumDeliveryDay = minDateStr
                date_state.maximumDeliveryDay = maxDateStr

                date_state.is_failed = false
            } catch (error) {
                console.error(error)
                date_state.is_failed = true
            } finally {
                date_state.is_loading = false
            }
        }

        onMounted(() => {
            loadMinimumDeliveryDay()
        })

        return {
            state,
            date_state,
            onDateChange,
            onTimeSlotChange,
            onSubmit,
        }
    }
})
